import '../../Styles/App.css';
import '../../Styles/Admin.css';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import izenuAPI from '../../services/izenuAPI';

const CDN = 'https://dpagz47n4g1lw.cloudfront.net';
const IMG_PROXY = 'https://img.gs/pqgzqcgmgv/100x100,quality=low';
const MIN_FOLLOWERS = 50;
const MIN_STREAMS = 500;
const MIN_AGE = 30;

function CriterionBadge({ value, min, label }) {
    const passed = value >= min;
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 8,
            backgroundColor: passed ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${passed ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
        }}>
            <span style={{ fontSize: 14 }}>{passed ? '✓' : '✗'}</span>
            <span style={{ fontSize: 12, color: passed ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                {value}
            </span>
            <span style={{ fontSize: 11, color: '#777' }}>/ {min} {label}</span>
        </div>
    );
}

function UserAvatar({ userId, photo, name }) {
    const [imgError, setImgError] = useState(false);
    const photoUrl = photo
        ? `${IMG_PROXY}/${CDN}/users/${userId}/profile/${photo}`
        : null;

    if (!photoUrl || imgError) {
        return (
            <div style={{
                width: 40, height: 40, borderRadius: '50%',
                backgroundColor: '#9333ea', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 700, color: '#fff', flexShrink: 0
            }}>
                {(name || '?')[0].toUpperCase()}
            </div>
        );
    }

    return (
        <img
            src={photoUrl}
            alt={name}
            onError={() => setImgError(true)}
            style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
        />
    );
}

export default function CuratorApplications() {
    const [applications, setApplications] = useState([]);
    const [filter, setFilter] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [actionNote, setActionNote] = useState('');
    const [selectedApp, setSelectedApp] = useState(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [expandedId, setExpandedId] = useState(null);

    const fetchApplications = useCallback(async () => {
        if (filter === 'earnings') return; // the Ganhos tab loads its own data
        setLoading(true);
        try {
            const r = await izenuAPI.get('/admin/curator/list.php', { params: { status: filter } });
            setApplications(r.data.data || []);
        } catch (err) {
            console.log(err);
        }
        setLoading(false);
    }, [filter]);

    useEffect(() => { fetchApplications(); }, [fetchApplications]);

    const handleApprove = async (app) => {
        if (!window.confirm(`Aprovar curadoria de "${app.playlist_title}" por ${app.user_name}?`)) return;
        try {
            await izenuAPI.post('/admin/curator/approve.php', {
                application_id: app.id,
                note: 'Aprovado pelo admin'
            });
            setApplications(prev => prev.filter(a => a.id !== app.id));
        } catch (err) {
            alert('Erro ao aprovar: ' + (err.response?.data?.message || err.message));
        }
    };

    const openRejectModal = (app) => {
        setSelectedApp(app);
        setActionNote('');
        setShowRejectModal(true);
    };

    const handleReject = async () => {
        if (!actionNote.trim()) { alert('Motivo é obrigatório'); return; }
        try {
            await izenuAPI.post('/admin/curator/reject.php', {
                application_id: selectedApp.id,
                note: actionNote
            });
            setApplications(prev => prev.filter(a => a.id !== selectedApp.id));
            setShowRejectModal(false);
        } catch (err) {
            alert('Erro ao rejeitar: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDeactivate = async (app) => {
        if (!window.confirm(`Desactivar curadoria de "${app.playlist_title}"?`)) return;
        try {
            await izenuAPI.post('/admin/curator/deactivate.php', {
                curator_playlist_id: app.id
            });
            setApplications(prev => prev.filter(a => a.id !== app.id));
        } catch (err) {
            alert('Erro ao desactivar: ' + (err.response?.data?.message || err.message));
        }
    };

    const statusBadge = (status) => {
        const colors = { pending: '#d97706', approved: '#16a34a', rejected: '#dc2626' };
        const labels = { pending: 'Pendente', approved: 'Aprovado', rejected: 'Rejeitado' };
        return (
            <span style={{
                backgroundColor: colors[status] + '20',
                color: colors[status],
                padding: '3px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600
            }}>
                {labels[status]}
            </span>
        );
    };

    const allCriteriaMet = (app) =>
        app.followers_count >= MIN_FOLLOWERS &&
        app.monthly_streams >= MIN_STREAMS &&
        app.playlist_age_days >= MIN_AGE;

    const tabCounts = { pending: 'Pendentes', approved: 'Aprovados', rejected: 'Rejeitados', earnings: 'Ganhos' };

    return (
        <div className="sm:p-6 text-left" style={{ color: '#222', maxWidth: 1200 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <span style={{ fontSize: 28, color: '#9333ea' }}>★</span>
                <h2 style={{ margin: 0, fontSize: 22, color: '#222' }}>Curadores de Playlists</h2>
            </div>
            <p style={{ margin: '0 0 20px', maxWidth: 720, fontSize: 14, lineHeight: 1.5, color: '#555', textAlign: 'left' }}>
                Quem tem uma playlist com {MIN_FOLLOWERS} seguidores, {MIN_STREAMS} reproduções por mês e pelo menos {MIN_AGE} dias
                pode candidatar-se a curador. Um curador aprovado recebe 2% dos royalties das reproduções feitas a partir
                dessa playlist; os 2% saem da parte da Izenu e o artista continua a receber 70%. Para gerar ganhos, a
                playlist também tem de estar verificada em «Verificar Playlists».
            </p>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
                {['pending', 'approved', 'rejected', 'earnings'].map(s => (
                    <button key={s} onClick={() => setFilter(s)} style={{
                        padding: '8px 24px', borderRadius: 20, cursor: 'pointer',
                        border: `1px solid ${filter === s ? '#9333ea' : '#e0e0e0'}`,
                        backgroundColor: filter === s ? '#9333ea' : '#fff',
                        color: filter === s ? '#fff' : '#555', fontWeight: 600, fontSize: 13,
                        transition: 'background-color 0.2s'
                    }}>
                        {tabCounts[s]}
                    </button>
                ))}
            </div>

            {filter === 'earnings' ? <CuratorEarnings /> : loading ? (
                <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>A carregar...</p>
            ) : applications.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>
                    <p style={{ fontSize: 40, marginBottom: 10 }}>★</p>
                    <p style={{ fontSize: 14 }}>Nenhuma candidatura {filter === 'pending' ? 'pendente' : filter === 'approved' ? 'aprovada' : 'rejeitada'}</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {applications.map(app => (
                        <div key={app.id} style={{
                            backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden',
                            border: `1px solid ${expandedId === app.id ? '#d8b4fe' : '#e0e0e0'}`,
                            transition: 'border-color 0.2s',
                        }}>
                            {/* Main row */}
                            <div
                                onClick={() => setExpandedId(expandedId === app.id ? null : app.id)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                                    cursor: 'pointer', flexWrap: 'wrap',
                                }}
                            >
                                {/* User avatar + info */}
                                <UserAvatar userId={app.user_id} photo={app.user_photo} name={app.user_name} />
                                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
                                        <span style={{ fontWeight: 700, fontSize: 15, color: '#222' }}>{app.user_name}</span>
                                        {statusBadge(app.status)}
                                        {allCriteriaMet(app) && (
                                            <span style={{
                                                backgroundColor: 'rgba(22,163,74,0.12)', color: '#16a34a',
                                                padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600
                                            }}>Elegível</span>
                                        )}
                                    </div>
                                    <div style={{ color: '#666', fontSize: 12, wordBreak: 'break-word' }}>
                                        {app.user_email} · Playlist: <strong style={{ color: '#333' }}>{app.playlist_title}</strong> · {new Date(app.created_at).toLocaleDateString('pt-PT')}
                                    </div>
                                </div>

                                {/* Criteria summary */}
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <CriterionBadge value={app.followers_count} min={MIN_FOLLOWERS} label="seg." />
                                    <CriterionBadge value={app.monthly_streams} min={MIN_STREAMS} label="str/m" />
                                    <CriterionBadge value={app.playlist_age_days} min={MIN_AGE} label="dias" />
                                </div>

                                {/* Expand arrow */}
                                <span style={{ color: '#999', fontSize: 18, marginLeft: 'auto', transition: 'transform 0.2s', transform: expandedId === app.id ? 'rotate(180deg)' : 'rotate(0)' }}>
                                    ▾
                                </span>
                            </div>

                            {/* Expanded detail */}
                            {expandedId === app.id && (
                                <div style={{
                                    borderTop: '1px solid #eee', padding: '16px',
                                    backgroundColor: '#fafafa',
                                }}>
                                    <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
                                        {/* Playlist info */}
                                        <div style={{ flex: '1 1 260px' }}>
                                            <h4 style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', marginBottom: 8, letterSpacing: 1 }}>Playlist</h4>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                {app.playlist_image ? (
                                                    <img
                                                        src={`${IMG_PROXY}/${CDN}/users/${app.user_id}/playlists/${app.playlist_image}`}
                                                        alt=""
                                                        style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }}
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        width: 48, height: 48, borderRadius: 8, backgroundColor: '#eee', color: '#999',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20
                                                    }}>♫</div>
                                                )}
                                                <div>
                                                    <div style={{ fontWeight: 600, fontSize: 15, color: '#222' }}>{app.playlist_title}</div>
                                                    <div style={{ color: '#777', fontSize: 12 }}>
                                                        ID: {app.playlist_id} · Criada há {app.playlist_age_days} dias
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Criteria detail */}
                                        <div style={{ flex: '1 1 260px' }}>
                                            <h4 style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', marginBottom: 8, letterSpacing: 1 }}>Critérios na candidatura</h4>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{ fontSize: 14, width: 20 }}>{app.followers_count >= MIN_FOLLOWERS ? '✓' : '✗'}</span>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{
                                                            height: 6, borderRadius: 3, backgroundColor: '#e5e7eb', overflow: 'hidden'
                                                        }}>
                                                            <div style={{
                                                                height: '100%', borderRadius: 3,
                                                                width: `${Math.min(100, (app.followers_count / MIN_FOLLOWERS) * 100)}%`,
                                                                backgroundColor: app.followers_count >= MIN_FOLLOWERS ? '#16a34a' : '#dc2626',
                                                                transition: 'width 0.3s'
                                                            }} />
                                                        </div>
                                                    </div>
                                                    <span style={{ color: '#444', fontSize: 12, minWidth: 80, textAlign: 'right' }}>
                                                        {app.followers_count} / {MIN_FOLLOWERS} seg.
                                                    </span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{ fontSize: 14, width: 20 }}>{app.monthly_streams >= MIN_STREAMS ? '✓' : '✗'}</span>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ height: 6, borderRadius: 3, backgroundColor: '#e5e7eb', overflow: 'hidden' }}>
                                                            <div style={{
                                                                height: '100%', borderRadius: 3,
                                                                width: `${Math.min(100, (app.monthly_streams / MIN_STREAMS) * 100)}%`,
                                                                backgroundColor: app.monthly_streams >= MIN_STREAMS ? '#16a34a' : '#dc2626',
                                                            }} />
                                                        </div>
                                                    </div>
                                                    <span style={{ color: '#444', fontSize: 12, minWidth: 80, textAlign: 'right' }}>
                                                        {app.monthly_streams} / {MIN_STREAMS} str/m
                                                    </span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{ fontSize: 14, width: 20 }}>{app.playlist_age_days >= MIN_AGE ? '✓' : '✗'}</span>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ height: 6, borderRadius: 3, backgroundColor: '#e5e7eb', overflow: 'hidden' }}>
                                                            <div style={{
                                                                height: '100%', borderRadius: 3,
                                                                width: `${Math.min(100, (app.playlist_age_days / MIN_AGE) * 100)}%`,
                                                                backgroundColor: app.playlist_age_days >= MIN_AGE ? '#16a34a' : '#dc2626',
                                                            }} />
                                                        </div>
                                                    </div>
                                                    <span style={{ color: '#444', fontSize: 12, minWidth: 80, textAlign: 'right' }}>
                                                        {app.playlist_age_days} / {MIN_AGE} dias
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Admin note if exists */}
                                    {app.admin_note && (
                                        <div style={{
                                            backgroundColor: '#f5f3ff', padding: '10px 14px', borderRadius: 8,
                                            marginBottom: 16, borderLeft: '3px solid #9333ea'
                                        }}>
                                            <span style={{ color: '#777', fontSize: 11 }}>Nota do admin: </span>
                                            <span style={{ color: '#333', fontSize: 13 }}>{app.admin_note}</span>
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                        {filter === 'pending' && (
                                            <>
                                                <button onClick={() => handleApprove(app)} style={{
                                                    padding: '8px 24px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                                    backgroundColor: '#16a34a', color: '#fff', fontSize: 13, fontWeight: 600
                                                }}>
                                                    ✓ Aprovar Curadoria
                                                </button>
                                                <button onClick={() => openRejectModal(app)} style={{
                                                    padding: '8px 24px', borderRadius: 8, border: '1px solid #dc2626', cursor: 'pointer',
                                                    backgroundColor: '#fff', color: '#dc2626', fontSize: 13, fontWeight: 600
                                                }}>
                                                    ✗ Rejeitar
                                                </button>
                                            </>
                                        )}
                                        {filter === 'approved' && (
                                            <button onClick={() => handleDeactivate(app)} style={{
                                                padding: '8px 24px', borderRadius: 8, border: '1px solid #d97706', cursor: 'pointer',
                                                backgroundColor: '#fff', color: '#d97706', fontSize: 13, fontWeight: 600
                                            }}>
                                                Desactivar Curadoria
                                            </button>
                                        )}
                                        {filter === 'rejected' && app.reviewed_at && (
                                            <span style={{ color: '#777', fontSize: 12 }}>
                                                Rejeitado em {new Date(app.reviewed_at).toLocaleDateString('pt-PT')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Reject modal */}
            {showRejectModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{ backgroundColor: '#fff', color: '#222', borderRadius: 12, padding: 24, width: 440, maxWidth: '90vw', border: '1px solid #e0e0e0', textAlign: 'left' }}>
                        <h3 style={{ marginBottom: 4, color: '#222' }}>Rejeitar candidatura</h3>
                        <p style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>
                            <strong style={{ color: '#222' }}>{selectedApp?.user_name}</strong> — Playlist: <strong style={{ color: '#222' }}>{selectedApp?.playlist_title}</strong>
                        </p>
                        <textarea
                            placeholder="Motivo da rejeição (obrigatório)..."
                            value={actionNote}
                            onChange={e => setActionNote(e.target.value)}
                            rows={3}
                            style={{
                                width: '100%', backgroundColor: '#fff', border: '1px solid #ccc',
                                borderRadius: 8, padding: 12, color: '#222', fontSize: 13, resize: 'vertical',
                                outline: 'none', boxSizing: 'border-box'
                            }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                            <button onClick={() => setShowRejectModal(false)} style={{
                                padding: '8px 20px', borderRadius: 8, border: '1px solid #ccc',
                                backgroundColor: '#fff', color: '#555', cursor: 'pointer', fontSize: 13
                            }}>Cancelar</button>
                            <button onClick={handleReject} style={{
                                padding: '8px 20px', borderRadius: 8, border: 'none',
                                backgroundColor: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13
                            }}>Rejeitar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const monthLabel = (ym) => { const [y, m] = ym.split('-').map(Number); return `${MONTHS[m - 1]} ${y}`; };
const usd = (n) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'USD' }).format(n || 0);
const kz = (n) => `${new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(n || 0)} Kz`;

/**
 * "Ganhos" tab: what each approved curator earned, month by month, from
 * curator_earnings. A month only appears once its royalty report has been
 * generated in Financeiro → Royalties, which is what calculates it.
 */
function CuratorEarnings() {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        izenuAPI.get('/admin/curator/earnings.php')
            .then(({ data }) => setRows(data.data || []))
            .catch(() => setError('Não foi possível carregar os ganhos.'));
    }, []);

    if (!rows) return <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>{error || 'A carregar...'}</p>;

    const byMonth = rows.reduce((acc, r) => { (acc[r.period] = acc[r.period] || []).push(r); return acc; }, {});

    return (
        <div className="space-y-5">
            <p className="text-sm text-gray-500">
                Os ganhos de um mês são calculados quando se gera o relatório desse mês em{' '}
                <Link to="/royalties" className="font-medium text-purple-700 underline">Financeiro → Royalties</Link>.
                Gerar o mesmo mês de novo recalcula-os. O CSV de curadores (com contactos) está nessa página.
            </p>
            {rows.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>
                    <p style={{ fontSize: 14 }}>Ainda não há ganhos calculados.</p>
                </div>
            ) : Object.entries(byMonth).map(([period, list]) => {
                const totalUsd = list.reduce((n, r) => n + r.usd, 0);
                const totalAoa = list.reduce((n, r) => n + r.aoa, 0);
                const streams = list.reduce((n, r) => n + r.streams, 0);
                return (
                    <section key={period} className="rounded-xl border border-gray-200 bg-white p-4">
                        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                            <h3 className="text-base font-semibold text-gray-900">{monthLabel(period)}</h3>
                            <p className="text-sm text-gray-600">
                                <span className="font-semibold text-gray-900">{usd(totalUsd)}</span> · {kz(totalAoa)} · {streams} {streams === 1 ? 'audição' : 'audições'}
                            </p>
                        </div>
                        <ul className="divide-y divide-gray-100 rounded-lg !border !border-solid border-gray-200 [&>li]:!p-0">
                            {list.map((r) => (
                                <li key={`${r.user_id}-${r.playlist_id}`}>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 text-sm">
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate font-medium text-gray-900">{r.curator || `#${r.user_id}`}</span>
                                            <span className="block truncate text-xs text-gray-500">{r.email}</span>
                                        </span>
                                        <span className="min-w-0 flex-1 truncate text-gray-700">{r.playlist || `Playlist #${r.playlist_id}`}</span>
                                        <span className="w-24 text-right text-xs text-gray-500">{r.streams} {r.streams === 1 ? 'audição' : 'audições'}</span>
                                        <span className="w-32 text-right">
                                            <span className="block font-semibold tabular-nums text-gray-900">{usd(r.usd)}</span>
                                            <span className="block text-xs text-gray-500">{kz(r.aoa)}</span>
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-2 text-xs text-gray-400">Calculado a {String(list[0].calculated_at).slice(0, 16)}</p>
                    </section>
                );
            })}
        </div>
    );
}
